import { useState, useEffect } from 'react';
import { staffAPI, placementAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import StaffNav from '../../components/StaffNav';
import {
  Briefcase, Loader2, Building2, MapPin,
  Clock, CheckCircle, XCircle, AlertCircle,
  ThumbsUp, ThumbsDown
} from 'lucide-react';

const statusConfig = {
  PENDING:   { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  APPROVED:  { label: 'Approved',       color: 'bg-green-100 text-green-700',   icon: CheckCircle },
  REJECTED:  { label: 'Rejected',       color: 'bg-red-100 text-red-700',       icon: XCircle },
  WITHDRAWN: { label: 'Withdrawn',      color: 'bg-gray-100 text-gray-500',     icon: XCircle },
};

const staffResponseConfig = {
  PENDING:  { label: 'Awaiting your response', color: 'bg-orange-100 text-orange-700' },
  ACCEPTED: { label: 'You accepted',           color: 'bg-green-100 text-green-700'  },
  DECLINED: { label: 'You declined',           color: 'bg-red-100 text-red-600'      },
};

export default function MyOpportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [respondModal, setRespondModal] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await staffAPI.getMyOpportunities();
      setOpportunities(res.data.data || []);
    } catch { toast.error('Failed to load opportunities'); }
    finally { setLoading(false); }
  };

  const handleRespond = async () => {
    if (!respondModal) return;
    setSubmitting(true);
    try {
      await placementAPI.respondToHireRequest(respondModal.id, {
        response: respondModal.response,
        note: note || null,
      });
      toast.success(
        respondModal.response === 'ACCEPTED'
          ? 'Request accepted! Admin will confirm your placement.'
          : 'Request declined.'
      );
      setRespondModal(null);
      setNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to respond');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];
  const filtered = filter === 'ALL' ? opportunities : opportunities.filter(o => o.status === filter);
  const orgLabel = (type) => type === 'HOSPITAL' ? 'Hospital' : 'Company';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <StaffNav title="My Opportunities" />
      <div className="max-w-4xl mx-auto px-6 py-8">

        <div className="bg-gradient-to-r from-slate-900 to-teal-900 rounded-2xl px-7 py-6 mb-8 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 85% 50%, rgba(20,184,166,0.25) 0%, transparent 60%)' }} />
          <div className="relative">
            <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.15em] mb-1">Direct Hire Requests</p>
            <h1 className="text-xl font-bold text-white">
              {opportunities.length > 0
                ? `${opportunities.length} organisation${opportunities.length > 1 ? 's' : ''} interested in you`
                : 'No hire requests yet'}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Accept or decline direct hire requests. Accepted requests go to admin for confirmation.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 mb-6">
          <AlertCircle className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" />
          <p className="text-xs text-teal-700">
            <span className="font-bold">Your privacy is protected.</span> Company contact details are shared only after admin approves the request.
          </p>
        </div>

        {opportunities.length > 0 && (
          <div className="flex gap-1.5 mb-6 flex-wrap">
            {tabs.map(tab => (
              <button key={tab} onClick={() => setFilter(tab)}
                className={`px-5 py-2 rounded-xl text-sm font-semibold transition
                  ${filter === tab ? 'bg-slate-900 text-white shadow' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {tab === 'ALL' ? `All (${opportunities.length})` : (statusConfig[tab]?.label || tab)}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm text-center py-20 px-6">
            <Briefcase className="h-14 w-14 text-teal-100 mx-auto mb-4" />
            <h3 className="font-bold text-gray-500 text-lg">
              {filter === 'ALL' ? 'No hire requests yet' : `No ${filter.toLowerCase()} requests`}
            </h3>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(opp => {
              const cfg = statusConfig[opp.status] || statusConfig.PENDING;
              const StatusIcon = cfg.icon;
              const staffResp = opp.staffResponse || 'PENDING';
              const respCfg = staffResponseConfig[staffResp] || staffResponseConfig.PENDING;
              const canRespond = opp.status === 'PENDING' && staffResp === 'PENDING';

              return (
                <div key={opp.hireRequestId}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center shrink-0">
                        <Building2 className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800">{opp.companyName}</h3>
                        <p className="text-xs text-gray-400 mt-0.5">{orgLabel(opp.organizationType)}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${cfg.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />{cfg.label}
                      </span>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${respCfg.color}`}>
                        {respCfg.label}
                      </span>
                    </div>
                  </div>

                  {(opp.companyCity || opp.companyState) && (
                    <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
                      <MapPin className="h-3.5 w-3.5 text-gray-400" />
                      {[opp.companyCity, opp.companyState].filter(Boolean).join(', ')}
                    </div>
                  )}

                  {opp.message && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3 mb-3">
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Message from company</p>
                      <p className="text-sm text-gray-700 italic">"{opp.message}"</p>
                    </div>
                  )}

                  {opp.status === 'REJECTED' && opp.adminNote && (
                    <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-3">
                      <p className="text-xs text-red-500 font-semibold uppercase tracking-wide mb-1">Admin note</p>
                      <p className="text-sm text-red-700">{opp.adminNote}</p>
                    </div>
                  )}

                  {opp.status === 'APPROVED' && (
                    <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 mb-3">
                      <p className="text-sm text-green-700 font-semibold">
                        ✅ Admin has confirmed your placement. Check My Placements for details.
                      </p>
                    </div>
                  )}

                  {canRespond && (
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => { setRespondModal({ id: opp.hireRequestId, response: 'DECLINED' }); setNote(''); }}
                        className="flex-1 flex items-center justify-center gap-2 border border-red-200
                          text-red-600 bg-red-50 py-2.5 rounded-xl font-bold text-sm hover:bg-red-100 transition">
                        <ThumbsDown className="h-4 w-4" /> Decline
                      </button>
                      <button
                        onClick={() => { setRespondModal({ id: opp.hireRequestId, response: 'ACCEPTED' }); setNote(''); }}
                        className="flex-1 flex items-center justify-center gap-2 bg-green-600
                          text-white py-2.5 rounded-xl font-bold text-sm hover:bg-green-700 transition">
                        <ThumbsUp className="h-4 w-4" /> Accept
                      </button>
                    </div>
                  )}

                  <p className="text-xs text-gray-400 mt-3">
                    Received {new Date(opp.requestedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {respondModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-slate-800 mb-1">
              {respondModal.response === 'ACCEPTED' ? 'Accept This Hire Request' : 'Decline This Hire Request'}
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              {respondModal.response === 'ACCEPTED'
                ? 'Once accepted, admin will review and confirm your placement.'
                : 'The company will be notified that you declined.'}
            </p>
            <textarea value={note} onChange={e => setNote(e.target.value)}
              placeholder={respondModal.response === 'ACCEPTED' ? 'Any questions or comments...' : 'Reason for declining (optional)...'}
              rows={2}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none mb-4" />
            <div className="flex gap-3">
              <button onClick={() => { setRespondModal(null); setNote(''); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleRespond} disabled={submitting}
                className={`flex-1 py-2.5 rounded-xl font-bold text-sm disabled:opacity-60
                  ${respondModal.response === 'ACCEPTED' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                {submitting ? 'Submitting...' : respondModal.response === 'ACCEPTED' ? 'Confirm Accept' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
